import React from "react";
import { Thermometer, Wind, Flame, Droplet, Zap, X } from "lucide-react";
import { Card, Input, Toggle, Textarea, PillGroup, TagGroup, Select } from "./FormComponents";

export const Step8 = ({ data, updateData, isLight }) => {
  const handleChange = (field, value) => {
    updateData("climateUtilities", { ...data, [field]: value });
  };

  const handleNestedChange = (parent, field, value) => {
    updateData("climateUtilities", {
      ...data,
      [parent]: {
        ...data[parent],
        [field]: value
      }
    });
  };

  // Keep `instructions` dict in sync with selected types: drop entries for unselected types
  // and clear "Other" entry when the otherType label is cleared.
  const syncTypes = (parent, newTypes) => {
    const current = data[parent] || {};
    const prevInstr = current.instructions || {};
    const nextInstr = {};
    newTypes.forEach((t) => {
      if (prevInstr[t] !== undefined) nextInstr[t] = prevInstr[t];
    });
    const updates = { types: newTypes, instructions: nextInstr };
    if (!newTypes.includes("Other")) updates.otherType = "";
    updateData("climateUtilities", { ...data, [parent]: { ...current, ...updates } });
  };

  const setInstruction = (parent, typeKey, text) => {
    const current = data[parent] || {};
    const nextInstr = { ...(current.instructions || {}), [typeKey]: text };
    updateData("climateUtilities", { ...data, [parent]: { ...current, instructions: nextInstr } });
  };

  // Types that already have a dedicated specialized block — skip the generic instructions field for these.
  const SKIP_HEATING_INSTR = new Set(["Radiator", "Underfloor heating"]);

  // Derived: if hot water uses an electrical panel method, the panel IS accessible
  // This keeps electricity.panelAccessible in sync even after a page refresh from DB
  const isHotWaterPanelMethod = data.hotWater.manualOperation &&
    (data.hotWater.activationMethod === "Electrical panel switch" ||
      data.hotWater.activationMethod === "Electrical panel button");

  return (
    <div className="flex flex-col gap-8">
      <Card title="Cooling" icon={Wind} subtitle="Describe the cooling options available at your property." isLight={isLight}>
        <div className="flex flex-col gap-8">
          <Toggle 
            label="Does the property have cooling?" 
            value={data.cooling.available}
            isLight={isLight}
            onChange={(val) => {
              const updates = { available: val };
              if (!val) {
                updates.types = [];
                updates.otherType = "";
                updates.instructions = {};
              }
              updateData("climateUtilities", {
                ...data,
                cooling: { ...data.cooling, ...updates }
              });
            }}
          />

          {data.cooling.available && (
            <div className="flex flex-col gap-8">
              <TagGroup
                label="How is the property cooled?" required
                options={["Air conditioning", "Fan", "Ceiling fan", "Other"]}
                value={data.cooling.types}
                isLight={isLight}
                onChange={(val) => syncTypes("cooling", val)}
              />

              {data.cooling.types.includes("Other") && (
                <Input
                  label="Other cooling methods"
                  placeholder="e.g., Portable AC, specialized fans..."
                  value={data.cooling.otherType}
                  isLight={isLight}
                  onChange={(e) => handleNestedChange("cooling", "otherType", e.target.value)}
                  required
                />
              )}

              {data.cooling.types.length > 0 && (
                <div className="flex flex-col gap-6">
                  {data.cooling.types.map((t) => {
                    const displayName = t === "Other"
                      ? (data.cooling.otherType?.trim() || "Other")
                      : t;
                    return (
                      <Textarea
                        key={`cooling-instr-${t}`}
                        label={`Instructions for ${displayName} (optional)`}
                        placeholder={`How do guests use the ${displayName.toLowerCase()}? e.g., remote location, recommended temperature...`}
                        value={data.cooling.instructions?.[t] || ""}
                        isLight={isLight}
                        onChange={(e) => setInstruction("cooling", t, e.target.value)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      <Card title="Heating" icon={Flame} subtitle="Tell us how your property is heated." isLight={isLight}>
        <div className="flex flex-col gap-8">
          <Toggle 
            label="Is heating available at the property?" 
            value={data.heating.available}
            isLight={isLight}
            onChange={(val) => {
              const updates = { available: val };
              if (!val) {
                updates.types = [];
                updates.otherType = "";
                updates.instructions = {};
                updates.radiator = { manual: false, instructions: "" };
                updates.underfloor = { manual: false, instructions: "" };
              }
              updateData("climateUtilities", {
                ...data,
                heating: { ...data.heating, ...updates }
              });
            }}
          />

          {data.heating.available && (
            <div className="flex flex-col gap-8">
              <TagGroup
                label="How is the property heated?" required
                options={["Air conditioning", "Radiator", "Space heater", "Underfloor heating", "Other"]}
                value={data.heating.types}
                isLight={isLight}
                onChange={(val) => syncTypes("heating", val)}
              />

              {data.heating.types.includes("Other") && (
                <Input
                  label="Other heating methods"
                  placeholder="e.g., Wood stove, specialized heaters..."
                  value={data.heating.otherType}
                  isLight={isLight}
                  onChange={(e) => handleNestedChange("heating", "otherType", e.target.value)}
                  required
                />
              )}

              {data.heating.types.some((t) => !SKIP_HEATING_INSTR.has(t)) && (
                <div className="flex flex-col gap-6">
                  {data.heating.types.filter((t) => !SKIP_HEATING_INSTR.has(t)).map((t) => {
                    const displayName = t === "Other"
                      ? (data.heating.otherType?.trim() || "Other")
                      : t;
                    return (
                      <Textarea
                        key={`heating-instr-${t}`}
                        label={`Instructions for ${displayName} (optional)`}
                        placeholder={`How do guests use the ${displayName.toLowerCase()}? e.g., remote location, recommended temperature...`}
                        value={data.heating.instructions?.[t] || ""}
                        isLight={isLight}
                        onChange={(e) => setInstruction("heating", t, e.target.value)}
                      />
                    );
                  })}
                </div>
              )}

              {data.heating.types.includes("Radiator") && (
                <div className={`flex flex-col gap-8 p-6 rounded-2xl border ${isLight ? 'bg-neutral-50 border-neutral-200' : 'bg-white/5 border-white/10'}`}>
                  <Toggle 
                    label="Do guests need to turn the radiator on manually?" 
                    value={data.heating.radiator.manual}
                    isLight={isLight}
                    onChange={(val) => {
                      const updates = { manual: val };
                      if (!val) updates.instructions = "";
                      handleNestedChange("heating", "radiator", { ...data.heating.radiator, ...updates });
                    }}
                  >
                    <Textarea 
                      label="How do guests turn it on?" placeholder="Set the dial on the radiator or use the thermostat..." required
                      value={data.heating.radiator.instructions}
                      isLight={isLight}
                      onChange={(e) => handleNestedChange("heating", "radiator", { ...data.heating.radiator, instructions: e.target.value })}
                    />
                  </Toggle>
                </div>
              )}

              {data.heating.types.includes("Underfloor heating") && (
                <div className={`flex flex-col gap-8 p-6 rounded-2xl border ${isLight ? 'bg-neutral-50 border-neutral-200' : 'bg-white/5 border-white/10'}`}>
                  <Toggle 
                    label="Do guests need to turn the underfloor heating on manually?" 
                    value={data.heating.underfloor.manual}
                    isLight={isLight}
                    onChange={(val) => {
                      const updates = { manual: val };
                      if (!val) updates.instructions = "";
                      handleNestedChange("heating", "underfloor", { ...data.heating.underfloor, ...updates });
                    }}
                  >
                    <Textarea 
                      label="How do guests turn it on?" placeholder="Use the floor heating controller in the hallway..." required
                      value={data.heating.underfloor.instructions}
                      isLight={isLight}
                      onChange={(e) => handleNestedChange("heating", "underfloor", { ...data.heating.underfloor, instructions: e.target.value })}
                    />
                  </Toggle>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      <Card title="Hot Water" icon={Droplet} subtitle="Let us know how hot water works at your property." isLight={isLight}>
        <div className="flex flex-col gap-8">
          <Toggle 
            label="Is hot water available?" 
            value={data.hotWater.available}
            isLight={isLight}
            onChange={(val) => {
              const updates = { available: val };
              if (!val) {
                updates.howProvided = "";
                updates.otherHowProvided = "";
                updates.manualOperation = false;
                updates.activationMethod = "";
                updates.panelLocation = "";
                updates.switchLocation = "";
                updates.labelForSwitch = false;
                updates.labelContent = "";
                updates.activationInstructions = "";
                updates.heatingTime = "";
                updates.turnOffInstructions = "";
              }
              updateData("climateUtilities", {
                ...data,
                hotWater: { ...data.hotWater, ...updates }
              });
            }}
          />
          {data.hotWater.available && (
            <div className="flex flex-col gap-8">
              <PillGroup 
                label="How is hot water provided?" required
                options={["Electric water heater", "Solar water heater", "Gas water heater", "Other"]} 
                value={data.hotWater.howProvided}
                isLight={isLight}
                onChange={(val) => handleNestedChange("hotWater", "howProvided", val)}
              />
              {data.hotWater.howProvided === "Other" && (
                <Input 
                  label="Please specify" placeholder="e.g., Central building heating..." required
                  value={data.hotWater.otherHowProvided} isLight={isLight}
                  onChange={(e) => handleNestedChange("hotWater", "otherHowProvided", e.target.value)}
                />
              )}
              <Toggle 
                label="Do guests need to turn the hot water on manually?" 
                value={data.hotWater.manualOperation}
                isLight={isLight}
                onChange={(val) => {
              const updates = { manualOperation: val };
              if (!val) {
                updates.activationMethod = "";
                updates.panelLocation = "";
                updates.switchLocation = "";
                updates.labelForSwitch = false;
                updates.labelContent = "";
                updates.activationInstructions = "";
              }
              updateData("climateUtilities", {
                ...data,
                hotWater: { ...data.hotWater, ...updates }
              });
            }}
              >
                <div className="flex flex-col gap-8">
                  <div className="flex flex-col gap-4">
                    <label className={`text-[10px] uppercase tracking-[0.18em] font-bold block ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                      How is the hot water turned on? <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {[
                        { id: "Electrical panel switch", label: "Electrical panel switch", desc: "A switch inside the electrical panel used to turn on the hot water." },
                        { id: "Electrical panel button", label: "Electrical panel button", desc: "A button inside the electrical panel used to turn on the hot water." },
                        { id: "Wall switch", label: "Wall switch", desc: "A wall-mounted switch, used to turn on the hot water." }
                      ].map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => {
                            const isPanelMethod = opt.id === "Electrical panel switch" || opt.id === "Electrical panel button";
                            const updates = {
                              hotWater: { ...data.hotWater, activationMethod: opt.id, panelLocation: isPanelMethod ? data.hotWater.panelLocation : "", switchLocation: isPanelMethod ? data.hotWater.switchLocation : "" }
                            };
                            // Always sync electricity.panelAccessible with the method selected
                            updates.electricity = {
                              ...data.electricity,
                              panelAccessible: isPanelMethod ? true : data.electricity.panelAccessible,
                              panelLocation: isPanelMethod ? data.hotWater.panelLocation : data.electricity.panelLocation
                            };
                            updateData("climateUtilities", { ...data, ...updates });
                          }}
                          className={`p-6 rounded-2xl border text-left flex flex-col gap-3 transition-all min-h-[140px] ${
                            data.hotWater.activationMethod === opt.id
                              ? (isLight ? "bg-neutral-900 text-white border-neutral-900 shadow-lg" : "bg-white text-neutral-900 border-white shadow-lg")
                              : (isLight ? "bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300" : "bg-white/5 text-neutral-400 border-white/10 hover:border-white/20")
                          }`}
                        >
                          <span className="font-bold text-sm leading-tight">{opt.label}</span>
                          <span className={`text-[11px] leading-relaxed ${data.hotWater.activationMethod === opt.id ? (isLight ? 'text-white/70' : 'text-neutral-400') : (isLight ? 'text-neutral-500' : 'text-neutral-500')}`}>{opt.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {(data.hotWater.activationMethod === "Electrical panel switch" || 
                    data.hotWater.activationMethod === "Electrical panel button") && (
                    <Input 
                      label="Electrical panel location" 
                      placeholder="In the hallway cupboard, next to the front door" 
                      required
                      value={data.hotWater.panelLocation}
                      isLight={isLight}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateData("climateUtilities", {
                          ...data,
                          hotWater: { ...data.hotWater, panelLocation: val },
                          electricity: { ...data.electricity, panelLocation: val }
                        });
                      }}
                    />
                  )}

                  {data.hotWater.activationMethod === "Wall switch" && (
                    <Input 
                      label="Wall switch location" 
                      placeholder="On the wall outside the bathroom" 
                      required
                      value={data.hotWater.switchLocation}
                      isLight={isLight}
                      onChange={(e) => handleNestedChange("hotWater", "switchLocation", e.target.value)}
                    />
                  )}

                  <Toggle 
                    label="Is there a label or visual indicator to identify the hot water switch?" 
                    value={data.hotWater.labelForSwitch}
                    isLight={isLight}
                    onChange={(val) => handleNestedChange("hotWater", "labelForSwitch", val)}
                  >
                    <Input 
                      label="How is it identified?" 
                      placeholder="HOT WATER" 
                      required
                      value={data.hotWater.labelContent}
                      isLight={isLight}
                      onChange={(e) => handleNestedChange("hotWater", "labelContent", e.target.value)}
                    />
                  </Toggle>

                  <Textarea 
                    label="Hot water activation instructions" placeholder="Turn the switch UP to turn it on..." required
                    value={data.hotWater.activationInstructions}
                    isLight={isLight}
                    onChange={(e) => handleNestedChange("hotWater", "activationInstructions", e.target.value)}
                  />
                </div>
              </Toggle>

              <div className="flex flex-col gap-6">
                <Input 
                  label="How long does it take for the hot water to heat up?" 
                  placeholder="15–20 minutes" 
                  required
                  value={data.hotWater.heatingTime}
                  isLight={isLight}
                  onChange={(e) => handleNestedChange("hotWater", "heatingTime", e.target.value)}
                />
                <Textarea 
                  label="Hot water turn-off instructions" 
                  placeholder="Turn the switch DOWN after use, or mention if it turns off automatically (e.g., 'The water heater turns off automatically after 20 minutes.')" 
                  required
                  value={data.hotWater.turnOffInstructions}
                  isLight={isLight}
                  onChange={(e) => handleNestedChange("hotWater", "turnOffInstructions", e.target.value)}
                />
              </div>
            </div>
          )}
        </div>
      </Card>

      <Card title="Electricity & Water" icon={Zap} subtitle="Utility details guests may need during their stay" isLight={isLight}>
        <div className="flex flex-col gap-8">
          <Toggle 
            label="Do guests need to insert a key card to activate the electricity?" 
            value={data.electricity.keyCardRequired}
            isLight={isLight}
            onChange={(val) => handleNestedChange("electricity", "keyCardRequired", val)}
          >
            <div className="flex flex-col gap-6 pt-4 border-t border-neutral-200/10">
              <PillGroup 
                label="Where can guests find the key card?" 
                required
                options={["Attached to the keys", "Provided separately"]} 
                value={data.electricity.keyCardLocation}
                isLight={isLight}
                onChange={(val) => handleNestedChange("electricity", "keyCardLocation", val)}
              />
              {data.electricity.keyCardLocation === "Provided separately" && (
                <Input 
                  label="Please specify where it can be found" 
                  placeholder="Inside the lockbox next to the front door" 
                  required
                  value={data.electricity.keyCardSeparatelyDetails}
                  isLight={isLight}
                  onChange={(e) => handleNestedChange("electricity", "keyCardSeparatelyDetails", e.target.value)}
                />
              )}
            </div>
          </Toggle>

          <div className="flex flex-col gap-2">
            <Toggle 
              label="Is the electrical panel accessible to guests?" 
              value={isHotWaterPanelMethod ? true : data.electricity.panelAccessible}
              isLight={isLight}
              onChange={(val) => handleNestedChange("electricity", "panelAccessible", val)}
              disabled={isHotWaterPanelMethod}
            >
              <Input 
                label="Electrical panel location" 
                placeholder="Inside the main entrance cupboard" 
                required
                value={data.electricity.panelLocation}
                isLight={isLight}
                onChange={(e) => handleNestedChange("electricity", "panelLocation", e.target.value)}
                disabled={isHotWaterPanelMethod}
              />
            </Toggle>
            {isHotWaterPanelMethod && (
              <p className={`text-[10px] font-bold uppercase tracking-wider ${isLight ? 'text-amber-600' : 'text-amber-500/80'}`}>
                Locked because you selected "{data.hotWater.activationMethod}" for hot water
              </p>
            )}
          </div>

          <Toggle 
            label="Are adapters available for guests using non-European plugs?" 
            value={data.electricity.adaptersAvailable}
            isLight={isLight}
            onChange={(val) => handleNestedChange("electricity", "adaptersAvailable", val)}
          >
            <Input 
              label="Where can guests find the plug adapters?" 
              placeholder="In the kitchen drawer or the bedside table" 
              required
              value={data.electricity.adapterLocation}
              isLight={isLight}
              onChange={(e) => handleNestedChange("electricity", "adapterLocation", e.target.value)}
            />
          </Toggle>

          <div className={`h-px w-full ${isLight ? 'bg-neutral-200' : 'bg-white/10'}`} />

          <div className="flex flex-col gap-8">
            <Toggle 
              label="Is the tap water drinkable?" 
              value={data.water.drinkable}
              isLight={isLight}
              onChange={(val) => handleNestedChange("water", "drinkable", val)}
            />

            <Toggle 
              label="Is the water shutoff location accessible to guests?" 
              value={data.water.shutoffAccessible}
              isLight={isLight}
              onChange={(val) => {
                const updates = { shutoffAccessible: val };
                if (!val) updates.shutoffLocation = "";
                updateData("climateUtilities", {
                  ...data,
                  water: { ...data.water, ...updates }
                });
              }}
            >
              <Input 
                label="Water shutoff location" 
                placeholder="Under the kitchen sink" 
                required
                value={data.water.shutoffLocation}
                isLight={isLight}
                onChange={(e) => handleNestedChange("water", "shutoffLocation", e.target.value)}
              />
            </Toggle>
          </div>
        </div>
      </Card>
    </div>
  );
};
